import { Box, Container, Divider, Grid, IconButton, Typography } from '@material-ui/core'
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles'
import {
    Add as AddIcon, BackspaceOutlined as BackspaceOutlinedIcon,
    Edit as EditIcon, Search as SearchIcon
} from '@material-ui/icons'
import MUIDataTable from "mui-datatables"
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useHistory } from "react-router-dom"
import SelectDate from '../../../components/Form/SelectDate'
import ButtonUI from '../../../components/ui/ButtonUI'
import LoadingUI from '../../../components/ui/LoadingUI'
import PageTitle from '../../../components/ui/PageTitleUI'
import { useAuth } from '../../../hooks/auth'
import { InfoConference100 } from '../../../interfaces/Mvm'
import MvmApi from '../../../services/MvmApi'
import getDefaultGridOptions from '../../../utils/componentUtils'
import utilsDate from '../../../utils/Date'
import permissions from '../../../utils/Permissions'
import { useStyles } from './Style'

const Conference100: React.FC = () => {
    const history = useHistory()
    const classes = useStyles()
    const [list, setList] = useState<any>([])
    const [loading, setLoading] = useState(true)
    const { user, hasPermission } = useAuth()
    const { t: tConference100 } = useTranslation("Conference100")
    const { t: tGeneral } = useTranslation("General")
    const [page, setPage] = useState(0)
    const [count, setCount] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    //Campos do Form
    interface IFormSearch {
        referenceDate?: string;
        startDate?: Date | null;
        endDate?: Date | null;
    }

    //Metodos do form:
    const { handleSubmit, reset, control, getValues, setValue } = useForm<IFormSearch>({
        defaultValues: {
            referenceDate: '',
            startDate: null,
            endDate: null
        }
    });

    useEffect(() => {
        if (!hasPermission(permissions.CONFERENCE.Search))
            history.push('/NoPermission')
    }, [history, user]);

    const columns = [
        {
            name: 'id',
            options: {
                display: false,
            }
        },
        {
            name: 'mvmShedId',
            options: {
                display: false,
            }
        },
        {
            name: 'mvmNumber',
            label: tConference100("mvmNumber"),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "left",
                    }),
                setCellProps: () => ({
                    align: "left"
                }),
            }
        },
        {
            name: "shedCode",
            label: tConference100("local"),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                    }),
                setCellProps: () => ({
                    align: "center"
                }),
            }
        },
       
        {
            name: "supplierCorporateName",
            label: tConference100("supplier"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },   
        {
            name: "conferenceTypeDescription",
            label: tConference100("conferenceType"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },  
        {
            name: "invoiceNumber",
            label: tConference100("invoiceNumber"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "partNumberCode",
            label: tConference100("partNumberCode"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "partNumberDescription",
            label: tConference100('partNumberDescription'),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "rackTypeDescription",
            label: tConference100("mdr"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "volumeQuantity",
            label: tConference100("volumeQuantity"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "declaredQuantity",
            label: tConference100('declaredQuantity'),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "receivedQuantity",
            label: tConference100('receivedQuantity'),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "delta",
            label: tConference100("delta"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return tableMeta.rowData[11] - tableMeta.rowData[12]
                }
            }
        },
        {
            name: "classification",
            label: tConference100("classification"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    var refValue = (tableMeta.rowData[11] - tableMeta.rowData[12])
                    return refValue > 0 ? tConference100("more") : 
                           refValue < 0 ? tConference100("less") :
                           "-"
                }
            }
        },
        {
            name: "DEEM",
            label: tConference100("deem"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "updatedBy",
            label: tGeneral('updatedBy'),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "updatedAt",
            label: tGeneral('updatedAt'),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                customBodyRender: (value: any) => {
                    return utilsDate.DateTimeShort(value);
                }
            }
        },
        {
            name: '',
            label: '',
            options: {
                sort: false,
                setCellProps: () => ({
                    align: "left"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return (
                        <>
                            <Box display="flex">
                                <IconButton disabled={!hasPermission(permissions.CONFERENCE.Add)} aria-label="Edit" color="primary" onClick={() => 
                                history.push('/Conference100-InsertUpdate?MvmShedId=' + tableMeta.rowData[1] +"&MvmConferenceId=" + tableMeta.rowData[0])
                                }>
                                    <EditIcon fontSize='small'/>                              
                                </IconButton>
                            </Box>
                        </>
                    );
                }
            }
        },
    ]

    function edit(id:string) {
        //history.push('/SubmitMvm/' + id)
    }

    useEffect(() => {
        onSubmit({
            DataInicial: '',
            DataFinal: '',
            UsuarioAprovador: '',
            IdPlanta: user.company.plantID,
            IdEmpresa: user.company.companyID,
        });
    }, []);

    const onSubmit = async (data: any) => {
        
        setLoading(true);
        const filtro: InfoConference100 = {
            companyId: Number(user.company.companyID),
            endDate: data?.endDate,
            pageIndex: page,
            pageSize: rowsPerPage,
            startDate: data?.startDate
        }

        MvmApi.searchConference100(filtro)
            .then(result => {
                setList(result.items);
                setCount(result.totalCount)
            }).catch(() => {
                setList([]);
            }).finally(() => {
                setLoading(false);
            });
    };

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''                    
                }
            },
        }
    })

    return (
        <Container maxWidth={false} component="main">
            <PageTitle title={tConference100("conference100Title")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>

                    <form translate={undefined} onSubmit={handleSubmit(onSubmit)}  >

                        <Grid container spacing={1}>
                            <Grid item xs={4} style={{ padding: "0px", display: 'flex', alignItems: 'center' }}>
                                <Typography className={classes.root}>
                                    {tGeneral("search")}
                                </Typography>

                            </Grid>

                            <Grid item xs={8} style={{ padding: "0px", textAlign: 'right' }}>
                                <ButtonUI onClick={() => history.push('/Conference100-Register') } startIcon={<AddIcon />}>{tGeneral("register")}</ButtonUI>
                                <ButtonUI onClick={() => reset()} startIcon={<BackspaceOutlinedIcon />}>{tGeneral("clean")}</ButtonUI>
                                <ButtonUI  type="submit" color="primary" startIcon={<SearchIcon />}>{tGeneral("search")}</ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '10px' }} />
                        
                        <Grid container spacing={1}>
                            <SelectDate
                                label={tConference100("referenceDate")} 
                                name="referenceDate" 
                                control={control} 
                                setValue={setValue}
                                style={{width: '100%'}}
                                enablePeriod 
                                dateAndTime={true}/>
                        </Grid>
                        
                    </form>
                
                    {
                        loading ? <LoadingUI /> :
                        <>
                            <Divider style={{ marginTop: '10px' }} />
                            
                            <p>{tGeneral("totalCount")} {("00" + list.length).slice(-2)}</p>
                            
                            <MuiThemeProvider theme={myTheme}>
                                <MUIDataTable
                                    title=""
                                    data={list}
                                    columns={columns}
                                    options={{
                                        ...getDefaultGridOptions(tGeneral),
                                        serverSide: true,
                                        pagination: true,
                                        sort: false,
                                        selectToolbarPlacement: "none",
                                        filter: false,
                                        search: false,
                                        download: false,
                                        print: false,
                                        viewColumns: false,
                                        selectableRows: 'none',
                                        page,
                                        count,
                                        rowsPerPage,                                        
                                        onChangePage: (pageIndex) => { setPage(pageIndex) },
                                        onChangeRowsPerPage: (rowsPerPage) => { setPage(0); setRowsPerPage(rowsPerPage) },
                                        }}
                                />
                            </MuiThemeProvider>
                        </>
                    }
                </div>
            </div>
        </Container>
    )
}
export default Conference100
